import { getNearbyPincodes as getNearbyPincodesFromService } from "./pincodeService";
import { DELHI_PINCODES } from "../data/delhiPincodes";

const NOMINATIM_URL = "https://nominatim.openstreetmap.org";
const UA = "PGApp/1.0 (pgfinder@example.com)";
const API_BASE_URL = "https://ciplpgapi.azurewebsites.net";

// Delhi bounding box
const DELHI_BBOX = "76.837,28.400,77.350,28.883";

// Cache for geocoding results
const geocodeCache = new Map();

// ========== Helper: Get accurate pincode from coordinates ==========
const getPincodeFromCoordinates = async (lat, lng) => {
  try {
    const reverseUrl = `${NOMINATIM_URL}/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1&zoom=18`;
    const response = await fetch(reverseUrl, {
      headers: { "User-Agent": UA, Accept: "application/json" },
    });
    const data = await response.json();

    return data?.address?.postcode || data?.address?.postal_code || null;
  } catch (error) {
    console.warn("Reverse geocode error:", error);
    return null;
  }
};

// ========== Helper: Find nearest pincode from database by distance ==========
const findNearestPincodeInDatabase = (lat, lng, maxDistanceKm = 3) => {
  let nearest = null;
  let minDistance = Infinity;

  for (const [pincode, data] of Object.entries(DELHI_PINCODES)) {
    // Calculate actual distance in km using Haversine formula
    const R = 6371;
    const dLat = ((data.lat - lat) * Math.PI) / 180;
    const dLon = ((data.lng - lng) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat * Math.PI) / 180) *
        Math.cos((data.lat * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    if (distance < minDistance && distance <= maxDistanceKm) {
      minDistance = distance;
      nearest = { pincode, area: data.area, distance };
    }
  }

  return nearest;
};

// ========== MAIN: Geocode place (completely dynamic) ==========
export const geocodePlace = async (query) => {
  const cacheKey = query.toLowerCase().trim();
  if (geocodeCache.has(cacheKey)) return geocodeCache.get(cacheKey);

  const searchQuery = query.trim();

  console.log(`\n🔍 Geocoding: "${searchQuery}"`);

  // Dynamic query contexts - try with Delhi suffix if not present
  const contextsToTry = [
    searchQuery,
    `${searchQuery}, Delhi`,
    `${searchQuery}, Delhi, India`,
  ];

  // Remove duplicates
  const uniqueContexts = [...new Set(contextsToTry)];

  let bestResult = null;
  let bestLat = null;
  let bestLng = null;

  for (const contextQuery of uniqueContexts) {
    const url = `${NOMINATIM_URL}/search?q=${encodeURIComponent(contextQuery)}&format=json&limit=3&addressdetails=1&bounded=1&viewbox=${DELHI_BBOX}&dedupe=1`;

    console.log(`  Trying: "${contextQuery}"`);

    try {
      const response = await fetch(url, {
        headers: { "User-Agent": UA, Accept: "application/json" },
      });

      if (!response.ok) continue;
      const data = await response.json();

      if (data && data.length > 0) {
        // Take the first result as best match (Nominatim ranks by relevance)
        bestResult = data[0];
        bestLat = parseFloat(bestResult.lat);
        bestLng = parseFloat(bestResult.lon);
        console.log(`    Found: ${bestResult.display_name?.split(",")[0]}`);
        break;
      }
    } catch (e) {
      console.warn(`    Error:`, e.message);
    }
  }

  if (!bestResult) {
    console.log(`❌ No results found for "${searchQuery}"`);
    return null;
  }

  console.log(`✅ Best match: ${bestResult.display_name?.split(",")[0]}`);
  console.log(`📍 Coordinates: ${bestLat}, ${bestLng}`);

  // Get accurate pincode via reverse geocoding
  let pincode = await getPincodeFromCoordinates(bestLat, bestLng);

  // If reverse geocode failed, try from forward result address
  if (!pincode && bestResult.address?.postcode) {
    pincode = bestResult.address.postcode;
  }

  // If still no pincode, find nearest by distance from database
  if (!pincode) {
    const nearest = findNearestPincodeInDatabase(bestLat, bestLng, 3);
    if (nearest) {
      pincode = nearest.pincode;
      console.log(
        `  📮 Using nearest database pincode: ${pincode} (${nearest.area}, ${nearest.distance.toFixed(1)}km away)`,
      );
    }
  } else {
    console.log(`  📮 Pincode: ${pincode}`);
  }

  const result = {
    lat: bestLat,
    lng: bestLng,
    pincode: pincode,
    label: bestResult.display_name?.split(",")[0] || searchQuery,
    fullAddress: bestResult.display_name,
  };

  console.log(
    `📍 Final: ${result.label} | Pincode: ${result.pincode || "NOT FOUND"}\n`,
  );

  geocodeCache.set(cacheKey, result);
  return result;
};

// ========== MAIN SEARCH FUNCTION ==========
export const performLocationSearch = async (
  searchQuery,
  gender,
  price,
  sharing,
  pgType = null,
  pgCity = "Delhi"
) => {
  console.log(`\n🔍 ========== NEW SEARCH ==========`);
  console.log(`🔎 "${searchQuery}"`);
  console.log(`📊 Filters: Gender=${gender}, Price=${price}, Sharing=${sharing}, PGType=${pgType}`);

  // Get pincode information to include in API payload
  let geoResult = null;
  let pincodeInfo = null;
  let allPincodes = null; // Initialize as null instead of empty array
  
  try {
    // Try to geocode to get pincode for API
    geoResult = await geocodePlace(searchQuery);
    if (geoResult && geoResult.pincode) {
      pincodeInfo = {
        pincode: geoResult.pincode,
        area: geoResult.label,
        coordinates: { lat: geoResult.lat, lng: geoResult.lng }
      };
      console.log(`📮📍 Pincode detected: ${geoResult.pincode} - ${geoResult.label}`);
      
      // Initialize array and add the main pincode
      allPincodes = [];
      allPincodes.push(geoResult.pincode);
      
      // Try to get nearby pincodes as well for logging and API
      try {
        const nearbyPincodes = await getNearbyPincodesFromService(geoResult.pincode, 3);
        if (nearbyPincodes && nearbyPincodes.length > 0) {
          console.log(`🔄 Nearby pincodes: ${nearbyPincodes.join(", ")}`);
          pincodeInfo.nearbyPincodes = nearbyPincodes;
          // Add nearby pincodes to the array (avoid duplicates)
          nearbyPincodes.forEach(p => {
            if (!allPincodes.includes(p)) {
              allPincodes.push(p);
            }
          });
        }
      } catch (error) {
        console.log("⚠️ Could not fetch nearby pincodes");
      }
    } else {
      console.log(`⚠️ No pincode found for "${searchQuery}"`);
      // allPincodes remains null
    }
  } catch (error) {
    console.log(`⚠️ Geocoding error:`, error.message);
    // allPincodes remains null
  }

  // ========== NEW API IMPLEMENTATION WITH PINCODE ==========
  // Prepare request body with SearchText, PGRentRange, PGType, and PinCode
  let requestBody = {
    SearchText: `${searchQuery}`,
    PGRentRange: price || null,
    PGType: pgType || null
  };
  
  // Add pincodes to request body if available, otherwise send null
  if (allPincodes && allPincodes.length > 0) {
    requestBody.PinCode = allPincodes;
    console.log(`📮 Including ${allPincodes.length} pincode(s) in API request:`, allPincodes);
  } else {
    requestBody.PinCode = null;
    console.log(`⚠️ No pincodes found - sending PinCode: null`);
  }

  console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  console.log("📍 SEARCH LOCATION:", searchQuery);
  if (pincodeInfo) {
    console.log("📮 Main Pincode:", pincodeInfo.pincode);
    console.log("📍 Area:", pincodeInfo.area);
    if (pincodeInfo.nearbyPincodes) {
      console.log("🔄 Nearby Pincodes:", pincodeInfo.nearbyPincodes);
    }
  } else {
    console.log("📮 Pincode: NOT FOUND for this location");
  }
  console.log("📨 API REQUEST BODY:", JSON.stringify(requestBody, null, 2));
  console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");

  try {
    // Call the new API endpoint with pincode data
    const apiResponse = await fetch(`${API_BASE_URL}/pgmaster/listing-landing-page`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    });
    
    if (!apiResponse.ok) {
      throw new Error(`API responded with status ${apiResponse.status}`);
    }
    
    const apiData = await apiResponse.json();
    console.log(`📦 API returned ${apiData?.length || 0} PGs`);
    
    // Log pincode information from the results if available
    if (apiData && Array.isArray(apiData) && apiData.length > 0) {
      const pincodesInResults = new Map();
      apiData.forEach(pg => {
        const pgPincode = pg.PinCode || pg.pincode || 'unknown';
        pincodesInResults.set(pgPincode, (pincodesInResults.get(pgPincode) || 0) + 1);
      });
      console.log("📊 Pincode distribution in results:");
      pincodesInResults.forEach((count, pincode) => {
        console.log(`   - ${pincode}: ${count} PG${count > 1 ? 's' : ''}`);
      });
    }
    
    return {
      success: true,
      results: apiData || [],
      resultCount: apiData?.length || 0,
      searchedLocation: searchQuery,
      appliedFilters: { gender, price, sharing, pgType, pgCity },
      // Include pincode info in response
      pincodeInfo: pincodeInfo,
      pincodesSent: allPincodes, // Will be null if no pincodes found, otherwise array
      // Additional metadata for the new API
      apiEndpoint: "listing-landing-page",
      requestBody: requestBody
    };
    
  } catch (error) {
    console.error("API Error:", error);
    return { 
      success: false, 
      error: "Failed to fetch PG listings", 
      resultCount: 0,
      details: error.message,
      searchedLocation: searchQuery,
      requestBody: requestBody,
      pincodeInfo: pincodeInfo,
      pincodesSent: allPincodes // Will be null if no pincodes found, otherwise array
    };
  }

  /* ========== OLD IMPLEMENTATION (COMMENTED OUT) ==========
  
  // Check if searching for entire Delhi
  const isWholeCitySearch = [
    "delhi",
    "new delhi",
    "delhi ncr",
    "delhi city",
  ].includes(searchQuery.toLowerCase().trim());

  if (isWholeCitySearch) {
    console.log(`📍 WHOLE DELHI SEARCH - No pincode filter`);
    
    const requestBody = {
      PGCity: pgCity,
      PGLocality: searchQuery,
      ...(gender && { Gender: gender }),
      ...(price && { PGRentRange: price }),
      ...(sharing && { SharingType: sharing }),
      ...(pgType && { PGType: pgType }),
    };

    console.log(`📨 Request body:`, requestBody);

    try {
      const apiResponse = await fetch(`${API_BASE_URL}/pgmaster/landing-page`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      });

      const apiData = await apiResponse.json();
      const resultCount = apiData?.length || 0;

      return {
        success: true,
        results: apiData || [],
        resultCount: resultCount,
        pincode: null,
        nearbyPincodes: [],
        coordinates: { lat: 28.6139, lng: 77.209 },
        locationName: "Delhi",
        searchedLocation: searchQuery,
        appliedFilters: { gender, price, sharing, pgType }
      };
    } catch (error) {
      console.error("API Error:", error);
      return {
        success: false,
        error: "Failed to fetch PG listings",
        resultCount: 0,
      };
    }
  }

  // Try to geocode but DON'T stop if it fails
  let geoResult = null;
  let geocodingFailed = false;
  
  try {
    geoResult = await geocodePlace(searchQuery);
    if (!geoResult) {
      geocodingFailed = true;
      console.log(`⚠️ Geocoding returned no result for "${searchQuery}", will proceed with search query as string`);
    }
  } catch (error) {
    geocodingFailed = true;
    console.log(`⚠️ Geocoding error for "${searchQuery}":`, error.message);
  }

  // Prepare request body - ALWAYS send to backend
  let requestBody = {
    PGCity: pgCity,
    PGLocality: searchQuery, // Always send the original search query
    ...(gender && { Gender: gender }),
    ...(price && { PGRentRange: price }),
    ...(sharing && { SharingType: sharing }),
    ...(pgType && { PGType: pgType }),
  };

  // If geocoding succeeded and we have pincodes, add them
  let allPincodes = [];
  let nearby = [];
  
  if (geoResult && geoResult.pincode) {
    try {
      nearby = await getNearbyPincodesFromService(geoResult.pincode, 3);
      allPincodes = [geoResult.pincode, ...nearby].filter(
        (v, i, a) => a.indexOf(v) === i
      );
      requestBody.PinCode = allPincodes;
      console.log("✅ Geocoding successful - including pincodes:", allPincodes);
    } catch (error) {
      console.log("⚠️ Failed to get nearby pincodes, continuing without pincode filter");
    }
  } else {
    console.log("⚠️ Geocoding failed or no pincode found - sending request without PinCode filter");
  }

  console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
  console.log("📍 SEARCH LOCATION:", searchQuery);
  console.log("📊 Geocoding Status:", geoResult ? "Success" : "Failed");
  if (geoResult) {
    console.log("📮 Main Pincode:", geoResult.pincode || "None");
    console.log("🔄 Nearby Pincodes:", nearby);
  }
  console.log("📨 FINAL REQUEST BODY:", JSON.stringify(requestBody, null, 2));
  console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");

  try {
    const apiResponse = await fetch(`${API_BASE_URL}/pgmaster/landing-page`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    });
    
    if (!apiResponse.ok) {
      throw new Error(`API responded with status ${apiResponse.status}`);
    }
    
    const apiData = await apiResponse.json();
    console.log(`📦 API returned ${apiData?.length || 0} PGs`);
    
    // Group results by pincode if available
    const resultsByPincode = {};
    if (apiData && Array.isArray(apiData)) {
      apiData.forEach(pg => {
        const pincode = pg.PinCode || pg.pincode || 'unknown';
        if (!resultsByPincode[pincode]) {
          resultsByPincode[pincode] = [];
        }
        resultsByPincode[pincode].push(pg);
      });
      if (Object.keys(resultsByPincode).length > 0) {
        console.log('📊 Results breakdown by pincode:', Object.keys(resultsByPincode).map(p => 
          `${p}: ${resultsByPincode[p].length} PGs`
        ));
      }
    }

    return {
      success: true,
      results: apiData || [],
      resultCount: apiData?.length || 0,
      pincode: geoResult?.pincode || null,
      nearbyPincodes: nearby,
      allSearchedPincodes: allPincodes,
      resultsByPincode: resultsByPincode,
      coordinates: geoResult ? { lat: geoResult.lat, lng: geoResult.lng } : null,
      locationName: geoResult?.label || searchQuery,
      searchedLocation: searchQuery,
      geocodingFailed: geocodingFailed,
      fullAddress: geoResult?.fullAddress || null,
      appliedFilters: { gender, price, sharing, pgType, pgCity }
    };
    
  } catch (error) {
    console.error("API Error:", error);
    return { 
      success: false, 
      error: "Failed to fetch PG listings", 
      resultCount: 0,
      details: error.message,
      searchedLocation: searchQuery,
      requestBody: requestBody
    };
  }
  
  ========== END OF OLD IMPLEMENTATION ========== */
};

// ========== SUGGESTIONS ==========
export const getLocationSuggestions = async (query, limit = 8) => {
  if (!query || query.length < 2) return [];

  const url = `${NOMINATIM_URL}/search?q=${encodeURIComponent(query)}&format=json&limit=${limit}&addressdetails=1&bounded=1&viewbox=${DELHI_BBOX}&dedupe=1`;

  try {
    const response = await fetch(url, {
      headers: { "User-Agent": UA, Accept: "application/json" },
    });
    const data = await response.json();

    return data.map((item) => ({
      id: item.place_id,
      name: item.display_name.split(",")[0],
      area: item.address?.suburb || item.address?.neighbourhood || "Delhi",
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
      pincode: item.address?.postcode || null,
      fullAddress: item.display_name,
    }));
  } catch (error) {
    console.error("Suggestions error:", error);
    return [];
  }
};